import React, { useEffect, useMemo, useState } from 'react';
import { Search, Plus, X } from 'lucide-react';
import { buildDateTokens } from '../../lib/dateTokens';

// Sprint F item 6.5 — token picker.
//
// Centered overlay (not anchored — was hard to position relative to a
// trigger that could be in the toolbar OR next to the subject input).
// Tokens are grouped by category and visible by default; the search box
// is a filter, not the primary affordance.
//
// Two token systems coexist in the codebase:
//   - flat:   {{principal_name}}   (send-one-off, template-renderer.js)
//   - dotted: {{account.name}}     (cron outreach runner, render-pdf.js)
// Both are listed so operators can pick whichever the surrounding
// context will substitute.

const STANDARD_TOKENS = [
  // Outreach campaign tokens (cron runner — dotted paths)
  { category: 'Campaign (outreach)', name: 'account.name',     sample: 'M.S. 308 Bronx Dance Academy', desc: 'School / account name' },
  { category: 'Campaign (outreach)', name: 'account.borough',  sample: 'Bronx' },
  { category: 'Campaign (outreach)', name: 'account.district', sample: '10' },
  { category: 'Campaign (outreach)', name: 'account.dbn',      sample: '10X308' },
  { category: 'Campaign (outreach)', name: 'contact.name',     sample: 'Sandra Sanchez', desc: 'Recipient full name' },
  { category: 'Campaign (outreach)', name: 'contact.email',    sample: 'principal@ms308.example' },
  { category: 'Campaign (outreach)', name: 'campaign.name',    sample: '2026-05 Monthly Check-in' },
  { category: 'Campaign (outreach)', name: 'unsubscribeUrl',   sample: 'https://nexsav.net/u/…', desc: 'Unsubscribe link' },

  // Send-one-off tokens (flat — template-renderer.js)
  { category: 'Recipient', name: 'principal_name',       sample: 'Sandra Sanchez' },
  { category: 'Recipient', name: 'principal_first_name', sample: 'Sandra' },
  { category: 'Recipient', name: 'principal_last_name',  sample: 'Sanchez' },
  { category: 'Recipient', name: 'salutation',           sample: 'Principal Sanchez' },
  { category: 'School',    name: 'school_name',          sample: 'M.S. 308 Bronx Dance Academy' },
  { category: 'School',    name: 'school_short_name',    sample: 'M.S. 308' },
  { category: 'School',    name: 'dbn',                  sample: '10X308' },
  { category: 'School',    name: 'borough',              sample: 'Bronx' },
  { category: 'School',    name: 'district',             sample: '10' },
  { category: 'Audit',     name: 'audit_score',          sample: '78' },
  { category: 'Audit',     name: 'audit_date',           sample: 'April 22' },
  { category: 'Sender',    name: 'sender_name',          sample: 'Jerrell Harrison' },
  { category: 'Sender',    name: 'sender_email',         sample: 'jerrell@nexsav.net' },
  { category: 'Sender',    name: 'sender_first_name',    sample: 'Jerrell' },
  { category: 'Sender',    name: 'unsubscribe_link',     sample: 'https://nexsav.net/unsub/…' },
  { category: 'Sender',    name: 'nexsav_address',       sample: '4747 197th Street, Flushing, NY 11358' },

  // Media package balance, looked up per school when the email is rendered.
  { category: 'Media',     name: 'media_sessions_remaining', sample: '3', desc: 'Sessions left across the school’s active media packages (0 if none)' },
  { category: 'Media',     name: 'media_package_expires',    sample: 'June 30, 2027', desc: 'When the package that expires soonest runs out' },
];

// Date tokens resolve when the email is sent, in New York time. The samples
// are today's real values so the picker reads naturally.
const DATE_SAMPLES = buildDateTokens(new Date());
const DATE_TOKENS = [
  { name: 'today',            desc: 'Today’s date' },
  { name: 'current_month',    desc: 'Month name' },
  { name: 'next_month' },
  { name: 'current_year' },
  { name: 'next_year' },
  { name: 'school_year',      desc: 'NYC school year, e.g. 2026–27' },
  { name: 'next_school_year' },
].map((t) => ({ category: 'Date', sample: DATE_SAMPLES[t.name], ...t }));

const CATEGORY_ORDER = ['Campaign (outreach)', 'Recipient', 'School', 'Audit', 'Date', 'Media', 'Sender', 'Campaign-custom'];

export default function TokenPickerPopover({ open, target, customTokens = [], onPick, onClose, onAddCustom }) {
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [draftKey, setDraftKey] = useState('');
  const [draftValue, setDraftValue] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const allTokens = useMemo(() => {
    const customs = customTokens.map((c) => ({
      category: 'Campaign-custom', name: c.key, sample: c.value,
    }));
    return [...STANDARD_TOKENS, ...DATE_TOKENS, ...customs];
  }, [customTokens]);

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = !needle
      ? allTokens
      : allTokens.filter((t) =>
          t.name.toLowerCase().includes(needle) ||
          t.category.toLowerCase().includes(needle) ||
          (t.desc || '').toLowerCase().includes(needle) ||
          String(t.sample || '').toLowerCase().includes(needle),
        );
    const byCat = {};
    for (const t of filtered) {
      if (!byCat[t.category]) byCat[t.category] = [];
      byCat[t.category].push(t);
    }
    const ordered = [];
    for (const cat of CATEGORY_ORDER) if (byCat[cat]) ordered.push([cat, byCat[cat]]);
    for (const cat of Object.keys(byCat)) if (!CATEGORY_ORDER.includes(cat)) ordered.push([cat, byCat[cat]]);
    return ordered;
  }, [allTokens, q]);

  if (!open) return null;

  const totalCount = grouped.reduce((n, [, items]) => n + items.length, 0);

  return (
    <div
      data-testid="token-picker-popover"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      onMouseDown={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-white/[0.08] bg-[#0a0a0f] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.7)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06] shrink-0">
          <div>
            <h3 className="text-[14px] font-semibold text-white">
              Insert token{target ? ` into ${target === 'subject' ? 'Subject' : 'Body'}` : ''}
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">Click any token to insert it at the cursor.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-2.5 border-b border-white/[0.05] flex items-center gap-2 shrink-0">
          <Search className="w-3.5 h-3.5 text-gray-500" />
          <input
            type="text"
            data-testid="token-picker-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter tokens by name, category, or sample…"
            className="flex-1 bg-transparent outline-none text-[13px] text-white placeholder-gray-600"
          />
          {q && (
            <button onClick={() => setQ('')} className="text-[11px] text-gray-500 hover:text-white">Clear</button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2">
          {totalCount === 0 && (
            <div className="px-3 py-6 text-center text-[12px] text-gray-500">No tokens match "{q}".</div>
          )}
          {grouped.map(([cat, items]) => (
            <div key={cat} className="mb-2">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                {cat}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 px-1">
                {items.map((t) => (
                  <button
                    key={`${t.category}-${t.name}`}
                    type="button"
                    data-testid={`token-pick-${t.name}`}
                    onClick={() => onPick?.(t.name)}
                    className="flex items-start justify-between gap-3 px-3 py-2 text-left rounded-lg hover:bg-white/[0.04] border border-transparent hover:border-white/[0.06]"
                    title={`Sample: ${t.sample}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] text-white font-mono truncate">{`{{${t.name}}}`}</div>
                      {t.desc && <div className="text-[10px] text-gray-500 mt-0.5">{t.desc}</div>}
                    </div>
                    <div className="text-[10px] text-gray-600 truncate max-w-[140px] shrink-0 mt-0.5">{t.sample}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-white/[0.06] p-3 shrink-0 bg-white/[0.01]">
          {!showAdd ? (
            <button
              type="button"
              data-testid="token-picker-add-custom"
              onClick={() => setShowAdd(true)}
              className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[12px] text-gray-300 hover:text-white border border-white/[0.06] hover:border-white/[0.12]"
            >
              <Plus className="w-3 h-3" /> Add custom token (campaign-scoped)
            </button>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="key (e.g., outreach_reason)"
                  value={draftKey}
                  onChange={(e) => setDraftKey(e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
                  className="px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-sm text-white"
                />
                <input
                  type="text"
                  placeholder="value"
                  value={draftValue}
                  onChange={(e) => setDraftValue(e.target.value)}
                  className="px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-sm text-white"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowAdd(false)} className="px-3 py-1 text-[12px] text-gray-400 hover:text-white">Cancel</button>
                <button
                  onClick={() => {
                    if (draftKey.trim()) {
                      onAddCustom?.({ key: draftKey.trim(), value: draftValue });
                      setShowAdd(false);
                      setDraftKey('');
                      setDraftValue('');
                    }
                  }}
                  className="px-3 py-1 rounded bg-[#B0FC38] text-gray-900 text-[12px] font-semibold"
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
